import React, { useState } from 'react';
import {
  addIcon,
  Button,
  PageContentResponsive,
  TeacherDashboardAssignments,
  TooltipHelp,
  TooltipHelpPlacements,
  TriggerIcons,
  Widget,
  Text,
  TableSimple,
  TableRowClickable,
  TableCellLink,
  ScorePercentage,
  ColorEmphasis,
  ColorEmphasisVariant,
  LoadingSpinner,
  WidgetError,
  retryIcon,
  WidgetEmptyState,
} from '@ctek/design-system';
import { getMultipleUsersById } from 'century-core/core-apis/accountsV2/users/users';
import { UserAssignment } from 'century-core/core-apis/assignment/types';
import { getAllUserAssignments } from 'century-core/core-apis/assignment/users';
import { useTranslateSubject } from 'century-core/core-components/Subject/SubjectLabel';
import { TableHeaderWithSorting } from 'century-core/core-components/TableHeaderWithSorting/TableHeaderWithSorting';
import { usePaginatedDataLoader } from 'century-core/core-utils/hooks/use-paginated-data-loader';
import { PaginatedRequest, PaginatedResult } from 'century-core/core-utils/shared/shared.types';
import { FormattedMessage } from 'react-intl';
import { Link, useRouteMatch } from 'react-router-dom';
import { getUserName } from 'century-core/core-utils/selectors/users';
import { formatDateAndTime } from 'century-core/core-utils/utils/date/date';
import { getPercentValue } from 'century-core/core-utils/utils/helpers';
import CenturyExactTime from 'century-core/core-components/CenturyTime/CenturyExactTime';
import { CreateStudentAssignmentModal } from './CreateStudentAssignmentModal';
import { MixpanelKeys } from 'century-core/core-utils/utils/mixpanel/MixpanelKeys';
import { MixpanelEventTypes } from 'century-core/core-utils/utils/mixpanel/MixpanelEventTypes';
import mixpanel from 'mixpanel-browser';
import { usePageViewEvent } from 'century-core/core-components/MixpanelUtils';
import { DashboardUserMode } from 'century-core/core-utils/utils/utils';
import { PaginationWithMixpanel } from 'century-core/core-components/PaginationWithMixpanel/PaginationWithMixpanel';
import { SortingDirection } from 'century-core/features-dashboard/TeacherDashboard/TeacherDashboardAssignmentsV3/helpers/helpers';
import { SmartAssignmentIcon } from 'century-core/features-dashboard/TeacherDashboard/TeacherDashboardAssignmentsV3/common/SmartAssignmentIcon';

export type PaginatedUserAssignment = PaginatedResult<UserAssignment> & { users: Ctek.User[] };

const ITEMS_PER_PAGE = 30;
const SORTABLE_COLUMNS = ['name', 'startAt', 'dueAt'];

type Props = {
  studentId: string;
  selectedStudent: string;
  userMode: DashboardUserMode;
};

const pageViewed = (userMode: DashboardUserMode) =>
  userMode === DashboardUserMode.GUARDIAN
    ? 'guardian-dependant-assignments'
    : userMode === DashboardUserMode.STUDENT
    ? 'student-dashboard-assignments'
    : userMode === DashboardUserMode.TEACHER
    ? 'teacher-student-dashboard-assignments'
    : '';

const StudentDashboardAssignments = (props: Props) => {
  const { studentId, selectedStudent, userMode } = props;
  const [createModalIsOpen, setCreateModalIsOpen] = useState(false);

  const match = useRouteMatch();
  const translateSubject = useTranslateSubject();
  usePageViewEvent(pageViewed(userMode));

  const isTeacher = userMode === DashboardUserMode.TEACHER;

  const listUserAssignments = React.useCallback(
    async (token: string, opts: PaginatedRequest, signal: AbortSignal) => {
      const assignments = await getAllUserAssignments(token, studentId, opts, signal);
      let users = [];
      if (isTeacher) {
        const assignedByIds = Array.from(new Set(assignments.data.map(a => a.assignment.assignedBy)));
        if (assignedByIds.length > 0) {
          const allUsersById = await getMultipleUsersById(assignedByIds, token, 'personal');
          users = Object.fromEntries(allUsersById.map(user => [user._id, user]));
        }
      }
      return { ...assignments, users };
    },
    [studentId, isTeacher]
  );

  const paginatedData = usePaginatedDataLoader<PaginatedUserAssignment>(listUserAssignments, {
    itemsPerPage: ITEMS_PER_PAGE,
    orderBy: 'dueAt',
    sortBy: 'desc',
    allowedSortColumns: SORTABLE_COLUMNS,
    syncWithUrl: true,
  });

  const { data: assignments, loading, error, handleSortingState } = paginatedData;

  if (loading && !assignments) {
    return (
      <WidgetWrapper studentName={selectedStudent} isTeacher={isTeacher}>
        <LoadingSpinner type="widget" overlay={true} />
      </WidgetWrapper>
    );
  }

  if (error) {
    return (
      <WidgetWrapper studentName={selectedStudent} isTeacher={isTeacher}>
        <WidgetError qa="widget-error">
          <WidgetError.Message qa="error-message">
            <FormattedMessage id="generic-error-message" defaultMessage="There was an error loading this data" />
          </WidgetError.Message>
          <WidgetError.Controls>
            <Button icon={retryIcon} size="small" variant="secondary" onClick={() => window.location.reload()}>
              <FormattedMessage id="error-reload-button-label" defaultMessage="Reload" />
            </Button>
          </WidgetError.Controls>
        </WidgetError>
      </WidgetWrapper>
    );
  }

  if (!assignments?.data?.length) {
    return (
      <>
        <WidgetWrapper studentName={selectedStudent} setCreateModalIsOpen={setCreateModalIsOpen} isTeacher={isTeacher}>
          <WidgetEmptyState hasPanel={true} qa="widget-empty-state">
            <WidgetEmptyState.Title>
              <FormattedMessage id="generic-no-data" defaultMessage="No data" />
            </WidgetEmptyState.Title>
            <WidgetEmptyState.Message>
              <FormattedMessage id="generic-no-data-message" defaultMessage="There is no data to display" />
            </WidgetEmptyState.Message>
          </WidgetEmptyState>
        </WidgetWrapper>
        {createModalIsOpen && (
          <CreateStudentAssignmentModal
            studentId={studentId}
            closeModal={() => setCreateModalIsOpen(false)}
            studentName={selectedStudent}
          />
        )}
      </>
    );
  }

  const handleStudentClick = (studentId: string, assignmentId: string, name: string) => {
    mixpanel.track(MixpanelEventTypes.StudentDashboardAssignmentClicked, {
      [MixpanelKeys.StudentId]: studentId,
      [MixpanelKeys.AssignmentId]: assignmentId,
      [MixpanelKeys.AssessmentName]: name,
    });
  };

  const handleSortingAndTracking = (col: string, sortingOrder: SortingDirection) => {
    handleSortingState(col, sortingOrder);
    mixpanel.track(MixpanelEventTypes.StudentDashboardAssignmentsTableSortChanged, {
      [MixpanelKeys.SortedColumn]: col,
      [MixpanelKeys.SortOrder]: sortingOrder,
    });
  };

  const trackPageNavigation = (navigatedValue: string) => {
    mixpanel.track(MixpanelEventTypes.StudentDashboardAssignmentsTableNavigationChanged, {
      [MixpanelKeys.Navigate]: navigatedValue,
    });
  };

  const hasSmartAssignments = assignments?.data?.some(a => a.kind === 'Smart') || false;

  return (
    <>
      <WidgetWrapper studentName={selectedStudent} setCreateModalIsOpen={setCreateModalIsOpen} isTeacher={isTeacher}>
        <TableSimple striped={true} hover={true} sticky={true} qa="assignments-table">
          <TableHeaderWithSorting
            headersData={generateAssignmentTableHeaders(hasSmartAssignments, isTeacher)}
            handleSortingAndTracking={handleSortingAndTracking}
            isSortingEnabled={true}
            sortableColumns={SORTABLE_COLUMNS}
            {...paginatedData}
          />
          <tbody
            data-testid="assignments-list-table-body"
            style={{ opacity: loading ? '0.5' : '1', transition: 'opacity 0.5s ease-in-out' }}
          >
            {assignments.data.map(item => {
              const { assignmentId, name, subject, nuggetIds, assignedBy, startAt, dueAt } = item.assignment;
              const { score, timeSpent } = item.result || {};

              return (
                <TableRowClickable key={assignmentId} qa="assignments-list-row">
                  <th>
                    <TableCellLink>
                      <Link
                        to={{
                          pathname: `${match.url}/${assignmentId}/markbook`,
                          state: { from: match.url },
                        }}
                        onClick={() => handleStudentClick(studentId, assignmentId, name)}
                        title={name}
                        data-testid="name"
                      >
                        {name}
                      </Link>
                    </TableCellLink>
                  </th>
                  {hasSmartAssignments && (
                    <td>
                      <SmartAssignmentIcon kind={item.kind} />
                    </td>
                  )}
                  <td>{translateSubject(subject?.name)}</td>
                  <td>
                    <FormattedMessage
                      id="nuggets-count"
                      defaultMessage="{totalNuggets, plural, one {# Nugget} other {# Nuggets}}"
                      values={{ totalNuggets: nuggetIds.length }}
                    />
                  </td>
                  {isTeacher && <td data-private={true}>{getUserName(paginatedData.data?.users[assignedBy])}</td>}
                  <td>{formatDateAndTime(startAt)}</td>
                  <td>{formatDateAndTime(dueAt)}</td>
                  <td>{getCompletionStatus(item)}</td>
                  <td>
                    {timeSpent?.seconds ? (
                      <ScorePercentage percentage={getPercentValue(score?.percentage)} hasBar={true} hasSymbol={true} hasNumber={true} />
                    ) : (
                      ''
                    )}
                  </td>
                  <td>{timeSpent?.seconds ? <CenturyExactTime time={timeSpent.seconds} shortenUnits={true} /> : ''}</td>
                </TableRowClickable>
              );
            })}
          </tbody>
        </TableSimple>
        <PaginationWithMixpanel trackPageNavigation={trackPageNavigation} {...paginatedData} />
      </WidgetWrapper>
      {createModalIsOpen && (
        <CreateStudentAssignmentModal studentId={studentId} closeModal={() => setCreateModalIsOpen(false)} studentName={selectedStudent} />
      )}
    </>
  );
};

export default StudentDashboardAssignments;

type WidgetWrapperProps = {
  studentName: string;
  isTeacher: boolean;
  children: React.ReactNode;
  setCreateModalIsOpen?: (open: boolean) => void;
};

const WidgetWrapper = (props: WidgetWrapperProps) => {
  const { studentName, isTeacher, children, setCreateModalIsOpen } = props;

  return (
    <PageContentResponsive>
      <TeacherDashboardAssignments>
        <Widget>
          <Widget.Header hasBorder={true}>
            <Widget.Title>
              {studentName ? (
                <FormattedMessage
                  id="assignment-title-label-plus-username"
                  defaultMessage="{studentName}'s Assignments"
                  values={{ studentName }}
                />
              ) : (
                <FormattedMessage id="my-dashboard-assignments-tab" defaultMessage="Assignments" />
              )}
              <span>&nbsp;</span>
              <TooltipHelp
                label={<FormattedMessage id="help-tooltip-label" defaultMessage="Help" />}
                placement={TooltipHelpPlacements.RIGHT_START}
                triggerIcon={TriggerIcons.INFO}
                qa="td-assignment-nuggets-widget-help-tooltip"
              >
                <Text.Lead>
                  <FormattedMessage
                    id="teacher-dashboard-assignments-tooltip"
                    defaultMessage="This table shows all assignments assigned to {studentName}. You can click on the assignment name to view the markbook."
                    values={{ studentName }}
                  />
                </Text.Lead>
              </TooltipHelp>
            </Widget.Title>
            {setCreateModalIsOpen && isTeacher && (
              <Widget.Controls>
                <Button
                  variant="secondary"
                  qa="create-assignment-button"
                  size="tiny"
                  icon={addIcon}
                  onClick={() => setCreateModalIsOpen(true)}
                >
                  <FormattedMessage id="teacher-dashboard-create-assignment-button" defaultMessage="Create Assignment" />
                </Button>
              </Widget.Controls>
            )}
          </Widget.Header>
          <Widget.Body>{children}</Widget.Body>
        </Widget>
      </TeacherDashboardAssignments>
    </PageContentResponsive>
  );
};

const generateAssignmentTableHeaders = (hasSmartIcon: boolean, isTeacher: boolean) => {
  const COLUMN_STRUCTURE = [
    { id: 'name', label: 'Assignment Name' },
    ...(hasSmartIcon ? [{ id: 'icon', label: ' ' }] : []),
    { id: 'subject', label: 'Subject' },
    { id: 'content', label: 'Content' },
    ...(isTeacher ? [{ id: 'assignedBy', label: 'Assigned By' }] : []),
    { id: 'startAt', label: 'Start' },
    { id: 'dueAt', label: 'Due' },
    { id: 'completedOn', label: 'Completed On' },
    { id: 'score', label: 'Score' },
    { id: 'timeSpent', label: 'Time Spent' },
  ];
  return COLUMN_STRUCTURE.map(column => ({
    col: column.id,
    columnMessageId: `assignments-table-header-${column.id}`,
    columnDefaultMessage: column.label,
  }));
};

const getCompletionStatus = (data: UserAssignment) => {
  const now = new Date();

  if (data.assignment.startAt && new Date(data.assignment.startAt) > now) {
    return (
      <ColorEmphasis variant={ColorEmphasisVariant.SECONDARY}>
        <span data-testid="scheduled">
          <FormattedMessage id="assignment-status-scheduled" defaultMessage="Scheduled" />
        </span>
      </ColorEmphasis>
    );
  }

  if (!data.result?.completedAt) {
    return (
      <ColorEmphasis variant={ColorEmphasisVariant.WARNING}>
        <span data-testid="not-completed">
          <FormattedMessage id="dependant-assignments-table-not-completed" defaultMessage="Not Completed" />
        </span>
      </ColorEmphasis>
    );
  }

  if (data.isOverdue) {
    return (
      <ColorEmphasis variant={ColorEmphasisVariant.WARNING}>
        <span data-testid="overdue">
          <FormattedMessage id="assignment-status-ended" defaultMessage="Overdue" />
        </span>
      </ColorEmphasis>
    );
  }

  return <span data-testid="completed">{formatDateAndTime(data.result.completedAt)}</span>;
};
